import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from '@/App';
import { GlobalErrorBoundary } from '@/app/GlobalErrorBoundary';
import { env } from '@/env';
import { listenForInstallPrompt } from '@/features/push/install-prompt';
import { capturePushLaunchFromLocation } from '@/features/push/push-launch';
import { registerServiceWorker } from '@/features/push/web-push-client';
import '@/i18n';
// Sentry must initialize before window handlers so unhandled errors fired
// during boot are captured. No-op when VITE_SENTRY_DSN is empty.
import { initSentry } from '@/lib/sentry';
import { installWindowErrorHandlers } from '@/lib/window-error-handler';

import './styles/globals.css';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

initSentry();
installWindowErrorHandlers();

// PWA: lift a notification tap's launch params off the URL before the router
// (or a login redirect) can drop them, and catch the one-shot install prompt.
capturePushLaunchFromLocation();
listenForInstallPrompt();

const bootstrap = async (): Promise<void> => {
  if (env.VITE_ENABLE_MOCKS) {
    const { startMocks } = await import('@/mocks/browser');
    await startMocks();
  }
  createRoot(rootElement).render(
    <StrictMode>
      <GlobalErrorBoundary>
        <App />
      </GlobalErrorBoundary>
    </StrictMode>,
  );
  // Push-only service worker (no caching). After first render so it never
  // competes with the app for the initial load; no-op in mocks mode.
  void registerServiceWorker();
};

void bootstrap();
